import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const AlgemeneVoorwaarden = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Algemene Voorwaarden | Dienstverlening & Verantwoordelijkheden | Compleet Verzekerd</title>
        <meta name="description" content="Algemene voorwaarden Compleet Verzekerd. Voorwaarden voor verzekeringsadvies, bemiddeling en dienstverlening. Wft-conform en transparant." />
        <meta name="keywords" content="algemene voorwaarden, voorwaarden dienstverlening, verzekeringsadvies voorwaarden, wft wet financieel toezicht, bemiddeling, aansprakelijkheid, geheimhouding" />
        <link rel="canonical" href="https://compleetverzekerd.nl/algemene-voorwaarden/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Algemene Voorwaarden",
            "description": "Algemene voorwaarden voor dienstverlening van Compleet Verzekerd",
            "publisher": {
              "@type": "Organization",
              "name": "Compleet Verzekerd"
            }
          })}
        </script>
      </Helmet>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded">
        Spring naar hoofdinhoud
      </a>
      <Header />
      
      <main id="main-content" className="pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-primary mb-4">Algemene Voorwaarden</h1>
            <p className="text-xl text-muted-foreground">
              Voorwaarden voor de dienstverlening van Compleet Verzekerd
            </p>
          </div>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 1 - Definities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p><strong>Compleet Verzekerd:</strong> De onderneming Compleet Verzekerd, gevestigd te Dordrecht.</p>
                  <p><strong>Opdrachtgever:</strong> De natuurlijke persoon of rechtspersoon die een opdracht verstrekt aan Compleet Verzekerd.</p>
                  <p><strong>Overeenkomst:</strong> De tussen Compleet Verzekerd en opdrachtgever gesloten overeenkomst tot het verlenen van diensten.</p>
                  <p><strong>Diensten:</strong> Alle werkzaamheden die Compleet Verzekerd voor opdrachtgever verricht, waaronder maar niet beperkt tot advies, bemiddeling en service bij verzekeringen.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 2 - Toepasselijkheid</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Deze algemene voorwaarden zijn van toepassing op alle overeenkomsten tussen 
                  Compleet Verzekerd en opdrachtgever, tenzij uitdrukkelijk schriftelijk anders 
                  is overeengekomen.
                </p>
                <p className="text-muted-foreground">
                  Door het aangaan van een overeenkomst met Compleet Verzekerd verklaart 
                  opdrachtgever zich akkoord met deze algemene voorwaarden.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 3 - Dienstverlening</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    3.1 Compleet Verzekerd verleent diensten op het gebied van verzekeringsadvies 
                    en -bemiddeling conform de bepalingen van de Wet op het financieel toezicht.
                  </p>
                  <p className="text-muted-foreground">
                    3.2 Compleet Verzekerd spant zich in om de dienstverlening naar beste kunnen 
                    uit te voeren, maar geeft geen garanties omtrent specifieke resultaten.
                  </p>
                  <p className="text-muted-foreground">
                    3.3 Alle adviezen worden gegeven op basis van de door opdrachtgever 
                    verstrekte informatie. Compleet Verzekerd is niet verantwoordelijk voor 
                    de juistheid en volledigheid van deze informatie.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 4 - Verplichtingen opdrachtgever</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    4.1 Opdrachtgever dient alle voor de uitvoering van de opdracht benodigde 
                    informatie tijdig en naar waarheid aan Compleet Verzekerd te verstrekken.
                  </p>
                  <p className="text-muted-foreground">
                    4.2 Opdrachtgever dient wijzigingen in zijn situatie die van belang kunnen 
                    zijn voor de verzekeringen tijdig door te geven aan Compleet Verzekerd.
                  </p>
                  <p className="text-muted-foreground">
                    4.3 Opdrachtgever is verantwoordelijk voor de tijdige betaling van premies 
                    en andere kosten aan verzekeraars.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 5 - Kosten en betaling</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    5.1 Voor de dienstverlening ontvangt Compleet Verzekerd een vergoeding 
                    van verzekeraars en/of een fee van opdrachtgever.
                  </p>
                  <p className="text-muted-foreground">
                    5.2 Eventuele kosten worden vooraf aan opdrachtgever meegedeeld.
                  </p>
                  <p className="text-muted-foreground">
                    5.3 Facturen dienen binnen 30 dagen na factuurdatum te worden betaald.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 6 - Aansprakelijkheid</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    6.1 Compleet Verzekerd is uitsluitend aansprakelijk voor schade die het 
                    directe gevolg is van een toerekenbare tekortkoming in de nakoming van 
                    de overeenkomst.
                  </p>
                  <p className="text-muted-foreground">
                    6.2 De aansprakelijkheid van Compleet Verzekerd is beperkt tot het bedrag 
                    dat door de beroepsaansprakelijkheidsverzekering wordt uitgekeerd.
                  </p>
                  <p className="text-muted-foreground">
                    6.3 Compleet Verzekerd is niet aansprakelijk voor indirecte schade, 
                    gevolgschade, gederfde winst of bedrijfsschade.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 7 - Geheimhouding</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Compleet Verzekerd verplicht zich tot geheimhouding van alle vertrouwelijke 
                  informatie die in het kader van de opdracht wordt verkregen, behoudens 
                  wettelijke verplichtingen tot het verstrekken van informatie.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 8 - Beëindiging</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    8.1 De overeenkomst kan door beide partijen worden beëindigd met 
                    inachtneming van een opzegtermijn van een maand.
                  </p>
                  <p className="text-muted-foreground">
                    8.2 Compleet Verzekerd heeft het recht de overeenkomst met onmiddellijke 
                    ingang te beëindigen indien opdrachtgever zijn verplichtingen niet nakomt.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 9 - Geschillen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Op alle overeenkomsten is Nederlands recht van toepassing. Geschillen worden 
                  voorgelegd aan de bevoegde Nederlandse rechter, tenzij partijen kiezen voor 
                  mediation of arbitrage.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Artikel 10 - Slotbepalingen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    10.1 Wijzigingen in deze voorwaarden zijn slechts geldig indien deze 
                    schriftelijk zijn overeengekomen.
                  </p>
                  <p className="text-muted-foreground">
                    10.2 Indien een bepaling in deze voorwaarden nietig is of wordt vernietigd, 
                    blijven de overige bepalingen onverminderd van kracht.
                  </p>
                  <p className="text-sm text-muted-foreground mt-6">
                    Deze algemene voorwaarden zijn voor het laatst gewijzigd op: {new Date().toLocaleDateString('nl-NL', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AlgemeneVoorwaarden;